import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { $downloadStatementStore, $initialCurrenciesAvailableStore, $profileFeatureStore, $statementsStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import { type StatementsRequest, type StatementsResponse, StatementType } from '@shared/api/rest/statements';
import { Tabs, TabsItem } from '@widgets';
import { ConsolidateTable } from '../statementsConsolidateTable';
import { AccountTable } from '../statementsAccountTable';
import { CardTable } from '../statementsCardTable';
import { Pagination, Select } from '@shared/widgets';

export const StatementsCardTab = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const statementsStore = useUnit($statementsStore.store);
  const downloadStatementStore = useUnit($downloadStatementStore.store);
  const statementFlow = profileFeatureStore.data?.statements;

  const [activeTab, setActiveTab] = useState<StatementType>(StatementType.CONSOLIDATED);
  const [currency, setCurrency] = useState<string>();
  const [index, setIndex] = useState<number>(1);
  const [count, setCount] = useState<number>(10);

  const { navigate } = useRouteHook();

  useEffect(() => {
    if (statementFlow?.viewOrDownloadConsolidatedStatement) setActiveTab(StatementType.CONSOLIDATED);
    else if (statementFlow?.viewOrDownloadAccountStatement) setActiveTab(StatementType.ACCOUNT);
    else if (statementFlow?.viewOrDownloadCardStatement) setActiveTab(StatementType.CARD);
    if (
      !statementFlow?.viewOrDownloadConsolidatedStatement &&
      !statementFlow?.viewOrDownloadAccountStatement &&
      !statementFlow?.viewOrDownloadCardStatement
    ) {
      navigate(RoutePaths.dashboard.onNavigate());
    }
  }, []);

  useEffect(() => {
    setIndex(1);
  }, [activeTab]);

  useEffect(() => {
    const request: StatementsRequest = {
      StatementType: activeTab,
      Currency: currency,
      FromIndex: (index - 1) * count,
      Count: count,
    };
    activeTab === StatementType.CONSOLIDATED && $statementsStore.events.get(request);
    activeTab === StatementType.ACCOUNT && $statementsStore.events.get(request);
    activeTab === StatementType.CARD && $statementsStore.events.get(request);
  }, [currency, index, count, activeTab]);

  const download = (statement: StatementsResponse) => {
    const dateParts = statement.date.split('-');
    $downloadStatementStore.events.downloadStatement({
      id: statement.statementId,
      statementType: statement.statementType,
      year: dateParts[0],
      monthNumber: dateParts[1],
      relationshipNumber: statement.relationshipNumber,
    });
  };

  if (
    !statementFlow?.viewOrDownloadConsolidatedStatement &&
    !statementFlow?.viewOrDownloadAccountStatement &&
    !statementFlow?.viewOrDownloadCardStatement
  )
    return null;
  return (
    <div className='mb-4'>
      <div className='flex justify-between items-center flex-nowrap'>
        <Tabs className='p-0'>
          {statementFlow?.viewOrDownloadConsolidatedStatement && (
            <TabsItem
              text='Consolidated statement'
              active={activeTab === StatementType.CONSOLIDATED}
              withBackground
              onClick={() => setActiveTab(StatementType.CONSOLIDATED)}
            />
          )}
          {statementFlow?.viewOrDownloadAccountStatement && (
            <TabsItem
              text='Account statement'
              active={activeTab === StatementType.ACCOUNT}
              withBackground
              onClick={() => setActiveTab(StatementType.ACCOUNT)}
            />
          )}
          {statementFlow?.viewOrDownloadCardStatement && (
            <TabsItem
              text='Card statement'
              active={activeTab === StatementType.CARD}
              withBackground
              onClick={() => setActiveTab(StatementType.CARD)}
            />
          )}
        </Tabs>
        <div className='flex items-center justify-end pb-6'>
          <Select
            className='min-w-[160px]'
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: 'All currencies' }}
            value={currency}
            onChange={setCurrency}
          />
        </div>
      </div>
      {activeTab === StatementType.CONSOLIDATED && statementFlow?.viewOrDownloadConsolidatedStatement && (
        <ConsolidateTable
          isLoading={statementsStore.loading}
          statements={statementsStore.data.data}
          onDownloadStatement={download}
          downloadingIds={downloadStatementStore.loadingIds}
        />
      )}
      {activeTab === StatementType.ACCOUNT && statementFlow?.viewOrDownloadAccountStatement && (
        <AccountTable
          isLoading={statementsStore.loading}
          statements={statementsStore.data.data}
          onDownloadStatement={download}
          downloadingIds={downloadStatementStore.loadingIds}
        />
      )}
      {activeTab === StatementType.CARD && statementFlow?.viewOrDownloadCardStatement && (
        <CardTable
          isLoading={statementsStore.loading}
          statements={statementsStore.data.data}
          onDownloadStatement={download}
          downloadingIds={downloadStatementStore.loadingIds}
        />
      )}
      <Pagination
        className='mt-6'
        currentPage={index}
        countItemPage={count}
        totalItemCount={statementsStore.data.total}
        onChangeCountItemPage={setCount}
        onChangePage={setIndex}
      />
    </div>
  );
};
